import { computeInteractionSideEffects } from 'features/libra/lifecycle/lifecycleSideEffects'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoPermissions } from 'features/permissions'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { deriveTicketPatch } from './deriveTicketPatch'
import { LibraTicket, UpdateTicketArgs } from './libraTicketsApi'
import {
  applyTicketPatch,
  commitTicketWrite,
  PendingTicketChange,
  reconcileTicketRefresh,
} from './ticketSnapshot'

/**
 * Function form of an enqueued write. Receives the latest committed ticket
 * snapshot (which the writer keeps in sync after each successful
 * `doUpdateTicket` call so write N sees write N-1's effects even when React /
 * SWR has not delivered the update back into the tree yet) and returns the
 * `UpdateTicketArgs` patch to issue. Return `null` to suppress the caller's
 * own patch — the writer may still emit a write if `applyInteractionSideEffects`
 * is left at its default `true` AND the merged side-effect patch (contributors
 * + FIRST_INTERACTION) is non-empty; otherwise the queue resolves the item
 * with no Rolodex call. Combine with `applyInteractionSideEffects: false` for
 * a guaranteed no-op (used by lifecycle-internal writes that shouldn't
 * retrigger FIRST_INTERACTION).
 */
export type TicketWriteFn = (
  latest: LibraTicket
) => Omit<UpdateTicketArgs, 'ticketId'> | null

export interface TicketWriterEnqueueOptions {
  /**
   * Controls whether contributor / `FIRST_INTERACTION` side effects merge
   * into this write. Defaults to `true` for substantive writes. Set to
   * `false` for lifecycle-internal writes (e.g. `completeWorkflow`, action
   * transitions) that ARE the lifecycle and shouldn't trigger
   * `FIRST_INTERACTION` re-entry.
   */
  applyInteractionSideEffects?: boolean
}

export interface TicketWriterContextType {
  committedTicket: LibraTicket
  getCommittedTicket: () => LibraTicket
  /**
   * Append a write to the per-ticket FIFO queue. Resolves with the
   * workflow-derived patch from the write fn, so callers can act
   * on the values actually applied without smuggling state out of the
   * closure. The result excludes interaction-side-effect fields; with those
   * enabled, the emitted write may also carry a `FIRST_INTERACTION`
   * `ticketStatus`. Resolves `null` when the write fn skipped (returned null)
   * or the item was dropped before its closure ran (unmount drain,
   * enqueue-after-unmount). Hard errors (network, schema) reject.
   */
  enqueue: (
    write: TicketWriteFn,
    opts?: TicketWriterEnqueueOptions
  ) => Promise<Omit<UpdateTicketArgs, 'ticketId'> | null>
  /** True while the queue has work in flight. */
  isSaving: boolean
}

const TicketWriterContext = createContext<TicketWriterContextType | null>(null)

interface QueueItem {
  write: TicketWriteFn
  opts: TicketWriterEnqueueOptions
  resolve: (applied: Omit<UpdateTicketArgs, 'ticketId'> | null) => void
  reject: (err: unknown) => void
}

export const TicketWriterProvider: FC<{
  children: ReactNode
  ticket: LibraTicket
  doUpdateTicket: (args: UpdateTicketArgs) => Promise<void>
}> = ({ children, ticket, doUpdateTicket }) => {
  const workflow = useWorkflow()
  const { user } = useGeckoPermissions()
  const committedTicketRef = useRef<LibraTicket>(ticket)
  const [committedTicket, setCommittedTicket] = useState(ticket)
  const queueRef = useRef<QueueItem[]>([])
  const pendingChangesRef = useRef<PendingTicketChange[]>([])
  const currentTicketIdRef = useRef(ticket.id)
  currentTicketIdRef.current = ticket.id
  const isFlushingRef = useRef(false)
  const unmountedRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)

  const publishCommittedTicket = useCallback((next: LibraTicket) => {
    committedTicketRef.current = next
    setCommittedTicket(next)
  }, [])
  const getCommittedTicket = useCallback(() => committedTicketRef.current, [])

  const previousTicketIdRef = useRef(ticket.id)
  if (committedTicketRef.current.id !== ticket.id) {
    // Descendants must see the new owner before the effect runs.
    committedTicketRef.current = ticket
    pendingChangesRef.current = []
  }
  const visibleCommittedTicket =
    committedTicket.id === ticket.id ? committedTicket : ticket

  useEffect(() => {
    if (previousTicketIdRef.current !== ticket.id) {
      previousTicketIdRef.current = ticket.id
      pendingChangesRef.current = []
      publishCommittedTicket(ticket)
      const pending = queueRef.current
      queueRef.current = []
      const dropError = new Error(
        'TicketWriter dropped queued write after ticket switch: ticketId changed mid-flow'
      )
      for (const item of pending) item.reject(dropError)
      isFlushingRef.current = false
      setIsSaving(false)
      return
    }

    const reconciled = reconcileTicketRefresh(ticket, pendingChangesRef.current)
    pendingChangesRef.current = reconciled.pendingChanges
    publishCommittedTicket(reconciled.ticket)
  }, [ticket, publishCommittedTicket])

  useEffect(() => {
    // Reset on setup so React 18 strict-mode's setup→cleanup→setup replay
    // doesn't leave the ref stuck at `true` for the remainder of the session.
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      // Resolve every pending write as a no-op so callers awaiting on
      // navigation away don't see an unhandled rejection.
      const pending = queueRef.current
      queueRef.current = []
      for (const item of pending) item.resolve(null)
    }
  }, [])

  const flush = useCallback(async () => {
    if (isFlushingRef.current) return
    isFlushingRef.current = true
    setIsSaving(true)
    try {
      while (queueRef.current.length > 0) {
        if (unmountedRef.current) break
        const item = queueRef.current.shift()
        if (!item) break
        try {
          const latest = committedTicketRef.current
          const patch = deriveTicketPatch(workflow, latest, item.write(latest))
          const sideEffects =
            item.opts.applyInteractionSideEffects === false
              ? {}
              : computeInteractionSideEffects({
                  lifecycleConfig: workflow.lifecycleConfig,
                  ticketStatus: latest.ticketStatus,
                  contributors: latest.contributors,
                  userId: user?.id,
                })

          const merged: Omit<UpdateTicketArgs, 'ticketId'> = {
            ...(patch ?? {}),
            ...(sideEffects.contributors !== undefined &&
            patch?.contributors === undefined
              ? { contributors: sideEffects.contributors }
              : {}),
            ...(sideEffects.ticketStatus !== undefined &&
            patch?.ticketStatus === undefined
              ? { ticketStatus: sideEffects.ticketStatus }
              : {}),
          }

          if (Object.keys(merged).length === 0) {
            item.resolve(patch)
            continue
          }

          await doUpdateTicket({ ticketId: latest.id, ...merged })

          // A completed write from the old ticket must not replace the new
          // ticket's committed snapshot.
          if (currentTicketIdRef.current !== latest.id) {
            item.resolve(patch)
            break
          }

          // Publish the acknowledged patch so the next queued write sees it.
          const appliedTicket = applyTicketPatch(latest, merged)
          const committed = commitTicketWrite(
            committedTicketRef.current,
            latest,
            appliedTicket,
            pendingChangesRef.current
          )
          pendingChangesRef.current = committed.pendingChanges
          publishCommittedTicket(committed.ticket)
          item.resolve(patch)
        } catch (err) {
          item.reject(err)
          const remaining = queueRef.current
          queueRef.current = []
          const abortError = new Error(
            'TicketWriter queue aborted after a prior write failed'
          )
          for (const pending of remaining) {
            pending.reject(abortError)
          }
          break
        }
      }
    } finally {
      isFlushingRef.current = false
      setIsSaving(false)
    }
  }, [doUpdateTicket, publishCommittedTicket, workflow, user?.id])

  const enqueue = useCallback(
    (write: TicketWriteFn, opts: TicketWriterEnqueueOptions = {}) =>
      new Promise<Omit<UpdateTicketArgs, 'ticketId'> | null>(
        (resolve, reject) => {
          if (unmountedRef.current) {
            resolve(null)
            return
          }
          queueRef.current.push({ write, opts, resolve, reject })
          void flush()
        }
      ),
    [flush]
  )

  const value = useMemo<TicketWriterContextType>(
    () => ({
      committedTicket: visibleCommittedTicket,
      getCommittedTicket,
      enqueue,
      isSaving,
    }),
    [visibleCommittedTicket, getCommittedTicket, enqueue, isSaving]
  )

  return (
    <TicketWriterContext.Provider value={value}>
      {children}
    </TicketWriterContext.Provider>
  )
}

export const useTicketWriter = (): TicketWriterContextType => {
  const ctx = useContext(TicketWriterContext)
  if (!ctx) {
    throw new Error(
      'useTicketWriter must be used within a TicketWriterProvider'
    )
  }
  return ctx
}
